import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { LessonAttachment } from "@/types/api";

interface LessonAttachmentsProps {
  attachments: LessonAttachment[];
  isChildTheme?: boolean;
}

const VIDEO_EXTS = new Set(["mp4", "mov", "webm", "avi", "mkv", "ogv", "m4v"]);

function fileExt(fileType: string, originalName: string): string {
  const e = (fileType || "").toLowerCase().replace(/^\./, "");
  if (e) return e;
  const m = (originalName || "").match(/\.([^.]+)$/);
  return m ? m[1].toLowerCase() : "";
}

function isVideo(fileType: string, originalName: string): boolean {
  return VIDEO_EXTS.has(fileExt(fileType, originalName));
}

export function LessonAttachments({ attachments, isChildTheme }: LessonAttachmentsProps) {
  if (!attachments.length) return null;

  return (
    <motion.section
      className="w-full max-w-lg mx-auto px-4 pb-6"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.15 }}
      aria-label="Lesson resources"
    >
      <h3
        className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3"
        style={{ fontFamily: "var(--lp-font-heading)" }}
      >
        Lesson Resources
      </h3>
      <ul className="flex flex-col gap-3">
        {attachments.map((file) =>
          isVideo(file.file_type, file.original_name) ? (
            <VideoCard key={file.id} file={file} isChildTheme={isChildTheme} />
          ) : (
            <DownloadCard key={file.id} file={file} isChildTheme={isChildTheme} />
          )
        )}
      </ul>
    </motion.section>
  );
}

// ─── Video card — inline player, no download ─────────────────────────────────

function VideoCard({ file, isChildTheme }: { file: LessonAttachment; isChildTheme?: boolean }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.li
      className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden"
      initial={false}
    >
      {/* Header row */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
        aria-expanded={expanded}
      >
        <div
          className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-lg select-none"
          style={{ background: "#fef9c3", color: "#ca8a04" }}
          aria-hidden
        >
          🎬
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800 truncate leading-tight">{file.label}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {fileExt(file.file_type, file.original_name).toUpperCase() || "Video"}
            {file.file_size > 0 && ` · ${formatSize(file.file_size)}`}
          </p>
        </div>
        <span
          className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow"
          style={{ background: "var(--lp-blue, #6366f1)" }}
          aria-hidden
        >
          {expanded ? (
            <>{isChildTheme ? "▲ Hide" : "▲ Hide"}</>
          ) : (
            <><PlayIcon /> {isChildTheme ? "Watch!" : "Watch"}</>
          )}
        </span>
      </button>

      {/* Collapsible video player */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="player"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3">
              <div
                className="relative w-full rounded-lg overflow-hidden bg-black"
                style={{ aspectRatio: "16/9" }}
              >
                <video
                  src={file.download_url}
                  className="absolute inset-0 w-full h-full"
                  controls
                  autoPlay
                  playsInline
                  {...({ controlsList: "nodownload" } as object)}
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.li>
  );
}

// ─── Download card — non-video files ─────────────────────────────────────────

function DownloadCard({ file, isChildTheme }: { file: LessonAttachment; isChildTheme?: boolean }) {
  const { icon, bg, color } = fileTypeMeta(file.file_type, file.original_name);

  return (
    <motion.li
      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm"
      whileHover={{ scale: isChildTheme ? 1.02 : 1.01, borderColor: "var(--lp-blue, #6366f1)" }}
      whileTap={{ scale: 0.99 }}
      transition={{ duration: 0.15 }}
    >
      <div
        className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold select-none"
        style={{ background: bg, color }}
        aria-hidden
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-800 truncate leading-tight">{file.label}</p>
        <p className="text-xs text-slate-400 mt-0.5">
          {fileExt(file.file_type, file.original_name).toUpperCase() || "File"}
          {file.file_size > 0 && ` · ${formatSize(file.file_size)}`}
        </p>
      </div>
      <a
        href={file.download_url}
        download={file.original_name || true}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow transition-opacity hover:opacity-90"
        style={{ background: "var(--lp-blue, #6366f1)" }}
        aria-label={`Download ${file.label}`}
      >
        <DownloadIcon />
        <span>Download</span>
      </a>
    </motion.li>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fileTypeMeta(fileType: string, originalName: string): { icon: string; bg: string; color: string } {
  const e = fileExt(fileType, originalName);
  if (e === "pdf") return { icon: "PDF", bg: "#fee2e2", color: "#dc2626" };
  if (e === "doc" || e === "docx") return { icon: "DOC", bg: "#dbeafe", color: "#2563eb" };
  if (e === "xls" || e === "xlsx") return { icon: "XLS", bg: "#dcfce7", color: "#16a34a" };
  if (e === "ppt" || e === "pptx") return { icon: "PPT", bg: "#ffedd5", color: "#ea580c" };
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(e))
    return { icon: "🖼️", bg: "#f3e8ff", color: "#7c3aed" };
  if (["mp3", "wav", "ogg", "m4a"].includes(e))
    return { icon: "🎵", bg: "#e0f2fe", color: "#0284c7" };
  if (e === "zip" || e === "rar" || e === "7z")
    return { icon: "📦", bg: "#f1f5f9", color: "#64748b" };
  return { icon: "📎", bg: "#f8fafc", color: "#475569" };
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function PlayIcon() {
  return (
    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 5v14l11-7L8 5z" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M8 12l4 4m0 0l4-4m-4 4V4" />
    </svg>
  );
}
